import * as React from "react";
import "./pdf-editor/pdfEditor.css";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
import { dateinameOhneSchraegstrich, hatSchraegstrich } from "../../utils/fileUtils";

/**
 * Dateiname in der Kopfleiste, der sich an Ort und Stelle umbenennen lässt.
 *
 * PO 28.09.2026: kein Dialog und kein Formularfeld — ein Klick auf den Namen
 * macht ihn selbst zum Kasten, in dem man tippt (wie das Textwerkzeug in
 * `tickets/screenshot/MarkierEditor.tsx`). Enter oder ein Klick daneben
 * übernimmt, Escape verwirft.
 *
 * Bearbeitet wird nur der Namensteil vor der Endung: Das Backend erzwingt die
 * Endung des Bestands ohnehin, ein mitgetipptes „.pdf“ würde nur verwirren.
 *
 * Ohne Fluent (User 04.10.2026): Klassen aus `pdfEditor.css` (`.pdfx-name`),
 * gedacht für die Kopfzeile der Leiste — die Schriftfarbe erbt er von dort.
 */

/** Zerlegt „Police 2026.pdf“ in Stamm und Endung (Endung inkl. Punkt, sonst leer). */
export function teileDateiname(fileName: string): { stamm: string; endung: string } {
    const punkt = fileName.lastIndexOf(".");
    if (punkt <= 0) return { stamm: fileName, endung: "" };
    return { stamm: fileName.slice(0, punkt), endung: fileName.slice(punkt) };
}

export const DateinameKasten: React.FC<{
    fileName: string;
    /** Speichert den neuen Namen; ein Fehler lässt den alten stehen. */
    onRename?: (neuerName: string) => Promise<void>;
}> = ({ fileName, onRename }) => {
    const ref = React.useRef<HTMLSpanElement | null>(null);
    const [editing, setEditing] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    // Escape schließt den Kasten; ein dabei nachgereichtes Blur darf den
    // getippten Namen dann nicht doch noch speichern.
    const verworfen = React.useRef(false);
    // T-456: Ein „/“ im getippten Namen wird zu „-“ — der Kasten sagt das sofort.
    const [mitStrich, setMitStrich] = React.useState(false);
    const { stamm, endung } = teileDateiname(fileName);

    // Beim Öffnen: Stamm hineinschreiben und komplett markieren — wie im
    // Windows-Explorer, man tippt sofort den neuen Namen.
    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!editing || !el) return;
        verworfen.current = false;
        setMitStrich(false);
        el.textContent = stamm;
        el.focus();
        const range = aktivesDokument().createRange();
        range.selectNodeContents(el);
        const sel = aktiveAuswahl();
        sel?.removeAllRanges();
        sel?.addRange(range);
        // Nur beim Öffnen — während des Tippens gehört der Inhalt dem Kasten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    const uebernehmen = React.useCallback(async () => {
        if (!editing || busy || verworfen.current) return;
        const neu = dateinameOhneSchraegstrich((ref.current?.textContent ?? "").replace(/\s+/g, " ").trim());
        if (!neu || neu === stamm || !onRename) {
            setEditing(false);
            return;
        }
        setBusy(true);
        try {
            await onRename(`${neu}${endung}`);
        } finally {
            setBusy(false);
            setEditing(false);
        }
    }, [editing, busy, stamm, endung, onRename]);

    if (!editing) {
        return (
            <span
                className="pdfx-name"
                title={onRename ? `${fileName} — zum Umbenennen klicken` : fileName}
                onClick={() => onRename && setEditing(true)}
                role={onRename ? "button" : undefined}
                tabIndex={onRename ? 0 : undefined}
                onKeyDown={(e) => {
                    if (onRename && (e.key === "Enter" || e.key === "F2")) {
                        e.preventDefault();
                        setEditing(true);
                    }
                }}
            >
                {stamm}
                <span className="pdfx-name-endung">{endung}</span>
            </span>
        );
    }

    return (
        <>
        <span
            ref={ref}
            className="pdfx-name editing"
            contentEditable={!busy}
            suppressContentEditableWarning
            spellCheck={false}
            aria-label="Dateiname"
            onBlur={() => void uebernehmen()}
            onInput={(e) => setMitStrich(hatSchraegstrich(e.currentTarget.textContent ?? ""))}
            onPaste={(e) => {
                // Nur Text — eingefügtes HTML hätte im Namen nichts verloren.
                e.preventDefault();
                aktivesDokument().execCommand("insertText", false, e.clipboardData.getData("text/plain").replace(/\s+/g, " "));
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    void uebernehmen();
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    verworfen.current = true;
                    setEditing(false);
                }
            }}
        />
        {mitStrich && (
            <span className="pdfx-name-hinweis" role="status">
                „/“ und „\“ sind nicht möglich – werden zu „-“
            </span>
        )}
        </>
    );
};
